import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { useSeo } from "../lib/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONFIG = {
  signature: {
    slug: "Signature",
    label: "Collection Signature",
    intro: "L'art parfumé européen",
    description:
      "Maisons françaises et européennes, héritage olfactif millimétré. Mauboussin, Jean Paul Gaultier, Volaré — ces créations incarnent l'élégance à la française et le savoir-faire des grands nez européens.",
    seoTitle: "Collection Signature — Parfums européens",
    seoDesc: "Parfums d'exception des grandes maisons françaises et européennes : Mauboussin, Jean Paul Gaultier, Volaré. Livraison offerte dès 60€ en France, dès 160€ à l'international.",
  },
  orient: {
    slug: "Orient",
    label: "Collection Orient",
    intro: "L'âme des déserts",
    description:
      "L'héritage arabo-persan sublimé. Lattafa, Paris Corner, Maison Alhambra, Loui Martin, Maison Asrar, French Avenue — des compositions ambrées, gourmandes, boisées, empreintes d'un raffinement millénaire.",
    seoTitle: "Collection Orient — Parfums Dubaï & Moyen-Orient",
    seoDesc: "Parfums orientaux d'exception : Lattafa, Paris Corner, Maison Alhambra, French Avenue. L'authenticité du Dubaï luxueux. Livraison offerte dès 60€ en France, dès 160€ à l'international.",
  },
};

export default function CollectionPage() {
  const { slug } = useParams();
  const cfg = CONFIG[slug?.toLowerCase()];
  const [products, setProducts] = useState(null);

  useSeo({
    title: cfg?.seoTitle ? `${cfg.seoTitle} · MAISON DOUBA` : "MAISON DOUBA",
    description: cfg?.seoDesc || "MAISON DOUBA — parfums de luxe.",
    canonical: cfg?.slug ? `https://wwwmaisondouba.com/collection/${slug}` : undefined,
  });

  useEffect(() => {
    if (!cfg) return;
    (async () => {
      const { data } = await axios.get(`${API}/products`, {
        params: { collection: cfg.slug },
      });
      setProducts(data || []);
    })();
  }, [cfg]);

  if (!cfg) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="pt-40 pb-24 px-6 text-center">
          <h1 className="font-serif text-4xl mb-4">Collection introuvable</h1>
          <Link to="/" className="text-[color:var(--gold)] hover:underline">Retour à l'accueil</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid={`collection-${slug.toLowerCase()}-page`}>
      <Navbar />

      {/* Header */}
      <section className="pt-40 pb-16 md:pb-24 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <p className="label-caps text-[color:var(--gold)] text-[11px] tracking-[0.3em] mb-6">{cfg.label}</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-tight mb-6">
            <em className="italic text-[color:var(--gold-soft)]">{cfg.intro}</em>
          </h1>
          <p className="text-white/50 font-light max-w-2xl mx-auto text-base leading-relaxed">{cfg.description}</p>
        </div>
      </section>

      {/* Products grid */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {products === null && (
            <p className="text-white/40 text-center">Chargement…</p>
          )}
          {products && products.length === 0 && (
            <p className="text-white/40 text-center py-20">Aucun produit dans cette collection pour le moment.</p>
          )}
          {products && products.length > 0 && (
            <>
              <p className="label-caps text-white/40 text-[10px] tracking-[0.3em] mb-10" data-testid="collection-count">
                {products.length} {products.length > 1 ? "parfums" : "parfum"}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                {products.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
